import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, Feather, Link as LinkIcon, Cog, Handshake } from "lucide-react";
import axios from "axios";
import PartnerIntakeForm from "@/components/PartnerIntakeForm";

const PublicPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/partners`);
        // Filter: Only show partners with showcase=true
        const showcased = res.data.filter(p => p.showcase);
        
        // Sort by order (Ascending: 0, 1, 2...)
        const sorted = showcased.sort((a, b) => (a.showcase_order || 0) - (b.showcase_order || 0));
        
        setPartners(sorted);
      } catch (error) {
        console.error("Failed to fetch partners", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // New Scenery Background
  const HEADER_BG = "https://images.unsplash.com/photo-1473448912268-2022ce9509d8?q=80&w=2667&auto=format&fit=crop";

  return (
    <div className="flex flex-col min-h-screen bg-zinc-50">
      {/* 1. Header & Value Proposition - Aligned with Proposal */}
      <div className="relative pt-32 pb-24 border-b">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HEADER_BG})` }} />
        <div className="absolute inset-0 bg-[#FDFBF7]/90" /> {/* Slight opacity to show texture but keep readable text */}

        <div className="container mx-auto px-4 text-center relative z-10">
          <h4 className="text-[#D97706] font-heading font-bold uppercase tracking-widest mb-4">A Vision for Partnership</h4>
          <h1 className="text-4xl md:text-6xl font-heading font-bold uppercase mb-8 text-black">
            Partner with <span className="text-[#D97706]">Frog Lake</span>
          </h1>
          <p className="text-gray-600 max-w-3xl mx-auto text-xl leading-relaxed mb-12 font-light">
            "We're building long-term, mutually beneficial partnerships that respect our land, culture, and community priorities."
          </p>

          <div className="text-center mb-16">
            <Button asChild size="lg" className="bg-[#D97706] hover:bg-[#B45309] text-white font-heading font-bold uppercase tracking-widest rounded-none px-10 py-6 text-sm shadow-lg">
              <Link to="/portal/login">Partner Portal Login</Link>
            </Button>
          </div>

          {/* Core Principles Grid (From Proposal) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto text-left">
            
            {/* Principle 1: Respect for Land & Culture */}
            <div className="bg-white p-8 rounded-none border-t-4 border-[#D97706] shadow-sm hover:shadow-md transition-shadow">
              <div className="h-20 w-20 bg-zinc-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Feather className="h-10 w-10 text-[#D97706]" />
              </div>
              <h3 className="font-heading font-bold text-xl uppercase mb-3 text-zinc-800 text-center">Respect for Land & Culture</h3>
              <p className="text-base text-gray-600 leading-relaxed text-center">
                Partnerships must honour our Treaty rights and deep spiritual connection to the land.
              </p>
            </div>

            {/* Principle 2: Shared, Long-Term Benefit */}
            <div className="bg-white p-8 rounded-none border-t-4 border-[#D97706] shadow-sm hover:shadow-md transition-shadow">
              <div className="h-20 w-20 bg-zinc-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                <LinkIcon className="h-10 w-10 text-[#D97706]" />
              </div>
              <h3 className="font-heading font-bold text-xl uppercase mb-3 text-zinc-800 text-center">Shared Benefit</h3>
              <p className="text-base text-gray-600 leading-relaxed text-center">
                We focus on projects that create sustainable revenue, capacity, and employment for our Nation.
              </p>
            </div>

            {/* Principle 3: Responsible Development */}
            <div className="bg-white p-8 rounded-none border-t-4 border-[#D97706] shadow-sm hover:shadow-md transition-shadow">
              <div className="h-20 w-20 bg-zinc-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Cog className="h-10 w-10 text-[#D97706]" />
              </div>
              <h3 className="font-heading font-bold text-xl uppercase mb-3 text-zinc-800 text-center">Responsible Development</h3>
              <p className="text-base text-gray-600 leading-relaxed text-center">
                Commitment to environmental stewardship and meticulous processes that ensure safety and quality.
              </p>
            </div>

            {/* Principle 4: Collaboration */}
            <div className="bg-white p-8 rounded-none border-t-4 border-[#D97706] shadow-sm hover:shadow-md transition-shadow">
              <div className="h-20 w-20 bg-zinc-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Handshake className="h-10 w-10 text-[#D97706]" />
              </div>
              <h3 className="font-heading font-bold text-xl uppercase mb-3 text-zinc-800 text-center">True Collaboration</h3>
              <p className="text-base text-gray-600 leading-relaxed text-center">
                Moving beyond one-off contracts to build relationships based on mutual respect and trust.
              </p>
            </div>

          </div>

          {/* New Section: Focus Areas (From Proposal) */}
          <div className="mt-24 text-center">
            <h4 className="text-[#D97706] font-heading font-bold uppercase tracking-widest mb-8">Where We Need Partners</h4>
            <h2 className="text-3xl font-heading font-bold uppercase mb-12">Our Focus Areas</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { title: "Energy & Resources", desc: "Traditional and new energy development projects." },
                { title: "Clean Energy", desc: "Environmental stewardship and renewable solutions." },
                { title: "Infrastructure", desc: "Housing, land development, and community facilities." },
                { title: "Business & Training", desc: "Employment capacity building and joint ventures." },
                { title: "Agriculture", desc: "Land-based ventures and sustainable farming." },
                { title: "Strategic Alliances", desc: "Regional partnerships that drive long-term growth." }
              ].map((item, i) => (
                <div key={i} className="bg-white p-6 border border-zinc-100 shadow-sm hover:border-[#D97706] transition-colors text-left">
                  <h3 className="font-heading font-bold text-lg uppercase mb-2 text-zinc-800">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* New Section: Partnership Process (From Proposal) */}
          <div className="mt-24 border-t border-gray-200 pt-20">
             <h2 className="text-3xl font-heading font-bold uppercase mb-12">How Partnership Works</h2>
             <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
               {[
                 { step: "01", title: "Introduce", text: "Submit your company profile via our secure portal." },
                 { step: "02", title: "Conversation", text: "We assess fit and alignment with our values." },
                 { step: "03", title: "Alignment", text: "Review against Frog Lake's strategic priorities." },
                 { step: "04", title: "Relationship", text: "Define the project and measure community benefit." }
               ].map((item) => (
                 <div key={item.step} className="text-left relative pl-8 border-l-2 border-zinc-200 hover:border-[#D97706] transition-colors py-2">
                   <span className="text-xs font-bold text-[#D97706] mb-1 block">{item.step}</span>
                   <h3 className="font-bold text-lg uppercase mb-2">{item.title}</h3>
                   <p className="text-sm text-gray-500">{item.text}</p>
                 </div>
               ))}
             </div>
          </div>

        </div>
      </div>

      {/* 2. Partner Intake Form - MOVED HERE */}
      <div className="container mx-auto px-4 py-16 text-center">
          <PartnerIntakeForm />
      </div>

      {/* 3. Partners Grid */}
      <div className="container mx-auto px-4 py-20 border-t border-gray-200">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-heading font-bold uppercase">MEET OUR PARTNERS</h2>
          <h3 className="text-lg font-bold text-gray-500 uppercase tracking-widest mt-2">Trusted Partners</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 border-t border-l border-gray-100">
          {partners.map((partner) => (
            <Link key={partner.id} to={`/partners/${partner.id}`} className="group block relative border-r border-b border-gray-100 aspect-[4/3] bg-white hover:z-10 hover:shadow-[0_0_30px_rgba(0,0,0,0.08)] transition-shadow">
              <div className="absolute inset-0 flex items-center justify-center p-10 transition-all duration-500 group-hover:scale-110">
                {partner.logo_url ? (
                  <img 
                    src={partner.logo_url.startsWith('http') ? partner.logo_url : `${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}`} 
                    alt={partner.name} 
                    className="max-h-full max-w-full object-contain filter"
                  />
                ) : (
                  <span className="text-sm font-bold text-gray-400 uppercase text-center tracking-widest">{partner.name}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
        
        {partners.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <p>No partners listed yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicPartners;
